import type { GuestDlClaims } from './guest-download-cookie';
import { REVIEW_OFFLINE_MESSAGE, ReviewActionError, reviewActionFallback } from './review-action-errors';

/**
 * Deleting a guest buyer's own review, both halves: the decision
 * /api/download/delete-guest-review makes about who may delete which review,
 * and the product page's call to that route.
 *
 * A guest review is products/{productId}/guestReviews/guest_{orderId}, written
 * only by the submitGuestReview callable, which authenticates on (orderId,
 * buyerEmail). The seller can read both of those in their own sales and
 * customer records, so a delete keyed on the same pair would let a seller
 * remove any buyer's bad review (see the comment in that callable). The proof
 * accepted here is the guestDlToken cookie instead (guest-download-cookie.ts),
 * which only a browser that completed checkout or opened the buyer's own magic
 * link can hold.
 *
 * Pure, and safe in the browser: the cookie is verified by the route, and only
 * its type is imported here.
 */

/**
 * Under /api/download because that is the cookie's Path, and the browser sends
 * it nowhere else. guest-review-delete.test.ts holds the two together.
 */
export const GUEST_REVIEW_DELETE_PATH = '/api/download/delete-guest-review';

export type GuestReviewTarget = { productId: string; orderId: string };

export type GuestReviewDeleteRefusal = {
  status: 401 | 403 | 404;
  reason: 'verify_required' | 'wrong_order' | 'not_in_order' | 'order_not_found';
  error: string;
};

/** The id submitGuestReview gives a guest review: one per order. */
export function guestReviewDocId(orderId: string): string {
  return `guest_${orderId}`;
}

const NOT_IN_ORDER: GuestReviewDeleteRefusal = {
  status: 403, reason: 'not_in_order', error: 'This product is not in that order.',
};

/**
 * Why this caller may NOT delete the guest review for `target`, or null when
 * they may. `readOrder` returns guestPurchases/{orderId} as it stands now (null
 * when it does not exist) and is only called once the cookie has been accepted.
 */
export async function guestReviewDeleteRefusal(
  claims: GuestDlClaims | null,
  target: GuestReviewTarget,
  readOrder: (orderId: string) => Promise<unknown>,
): Promise<GuestReviewDeleteRefusal | null> {
  // The cookie alone settles these, before any read, so a caller without the
  // proof learns nothing about whether the order exists. The type matters:
  // verifyGuestToken's one-shot dlInitToken carries the same claims under the
  // same secret, and becomes a cookie only through issue-download-token.
  if (!claims || claims.type !== 'guest_dl') {
    return { status: 401, reason: 'verify_required', error: 'Open your access link to confirm this purchase.' };
  }
  if (claims.piId !== target.orderId) {
    return { status: 403, reason: 'wrong_order', error: 'Your access link is for a different order.' };
  }
  if (!claims.productIds.includes(target.productId)) return NOT_IN_ORDER;

  // Then the order as it is now, the same defence in depth POST /api/download
  // applies: it still exists, under the email the cookie was minted for, and
  // it holds the product.
  const purchase = await readOrder(target.orderId);
  if (!purchase || typeof purchase !== 'object') {
    return { status: 404, reason: 'order_not_found', error: 'Purchase not found.' };
  }
  const { buyerEmail, items } = purchase as { buyerEmail?: unknown; items?: unknown };
  const orderEmail = typeof buyerEmail === 'string' ? buyerEmail.toLowerCase() : '';
  if (!orderEmail || orderEmail !== String(claims.email).toLowerCase()) {
    return { status: 403, reason: 'wrong_order', error: 'Your access link does not match this order.' };
  }
  const inOrder = Array.isArray(items) && items.some((item) =>
    !!item && typeof item === 'object' && (item as { productId?: unknown }).productId === target.productId);
  if (!inOrder) return NOT_IN_ORDER;
  // No refund check, on purpose. An account buyer can delete their own review
  // whatever happened to the purchase (the rules' delete branch checks only
  // authorship), and a refund must not trap a guest's review on the page.
  return null;
}

export const GUEST_REVIEW_VERIFY_MESSAGE =
  "We need to confirm it's you first. Open the access link from your purchase email in this browser, then try again.";

/** What the review dialog says when the route refuses with `status`. */
export function guestReviewDeleteFailureMessage(status: number): string {
  // 401: no cookie, or an expired one (it lasts an hour). 403: a cookie for
  // another of the guest's orders, since a browser holds one at a time. Either
  // way, opening the access link for THIS order mints the right one.
  if (status === 401 || status === 403) return GUEST_REVIEW_VERIFY_MESSAGE;
  if (status === 429) return 'Too many attempts. Wait a few minutes and try again.';
  return reviewActionFallback('delete');
}

/**
 * The product page's guest Delete. Resolves once the review is gone; throws a
 * ReviewActionError worded for the buyer otherwise.
 */
export async function deleteGuestReview(target: GuestReviewTarget, fetchImpl: typeof fetch = fetch): Promise<void> {
  let res: Response;
  try {
    res = await fetchImpl(GUEST_REVIEW_DELETE_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // The proof is the httpOnly cookie, which only a credentialed request to
      // its own path carries; the page can never read it.
      credentials: 'include',
      body: JSON.stringify({ productId: target.productId, orderId: target.orderId }),
    });
  } catch {
    throw new ReviewActionError(REVIEW_OFFLINE_MESSAGE);
  }
  if (!res.ok) throw new ReviewActionError(guestReviewDeleteFailureMessage(res.status));
}
